import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiAward,
  FiCheck,
  FiCheckCircle,
  FiDollarSign,
  FiHeart,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
  FiStar,
  FiTarget,
  FiUsers,
  FiZap,
  FiMessageCircle,
  FiPrinter,
  FiMonitor,
  FiTv,
  FiCalendar,
  FiPackage,
  FiEye,
  FiThumbsUp,
  FiMenu,
  FiX,
  FiChevronUp,
  FiInstagram,
  FiLinkedin,
  FiGlobe,
  FiImage,
  FiGrid,
  FiLayout,
  FiVideo,
  FiCamera,
  FiChevronRight,
} from "react-icons/fi";
import { pexelsImages } from "../assets/images/pexelsImages";
import { useGetGalleryItemsQuery, useSubmitContactFormMutation } from "../services/api";
import logo from "../assets/melona logo.png";
import banner from "../assets/banner.jpg";

// ── Portfolio Data ────────────────────────────────────────────
const PORTFOLIO_ITEMS = [
  { id: 1, category: "branding", title: "TechStart Ethiopia Brand Identity", image: pexelsImages.sampleOne, tags: ["Logo", "Branding"] },
  { id: 2, category: "printing", title: "Café Addis Menu & Flyers", image: pexelsImages.hero, tags: ["Menu", "Flyers"] },
  { id: 3, category: "advertising", title: "Urban Lightbox Campaign", image: pexelsImages.sampleTwo, tags: ["Lightbox", "Outdoor"] },
  { id: 4, category: "digital", title: "Fashion House Social Campaign", image: pexelsImages.sampleThree, tags: ["Social Media", "Ads"] },
  { id: 5, category: "branding", title: "Green Valley Corporate Kit", image: pexelsImages.hero, tags: ["Business Cards", "Letterhead"] },
  { id: 6, category: "printing", title: "Annual Conference Banners", image: pexelsImages.sampleOne, tags: ["Banners", "Backdrops"] },
  { id: 7, category: "advertising", title: "Vehicle Fleet Branding", image: pexelsImages.sampleTwo, tags: ["Vehicle Wrap"] },
  { id: 8, category: "digital", title: "Restaurant Launch Campaign", image: pexelsImages.sampleThree, tags: ["Facebook Ads", "Instagram"] },
];

const CATEGORY_ICONS = {
  branding: <FiLayout />,
  printing: <FiPrinter />,
  advertising: <FiTv />,
  digital: <FiMonitor />,
  portfolio: <FiImage />,
};

const normalizeCategoryId = (category = "Portfolio") =>
  category.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "portfolio";

const normalizePortfolioItem = (item) => {
  const categoryLabel = item.category || "Portfolio";
  const category = normalizeCategoryId(categoryLabel);

  return {
    id: item.id,
    category,
    categoryLabel,
    title: item.title,
    image: item.image_url || item.image,
    description: item.description || "",
    tags: item.tags || [categoryLabel, item.featured ? "Featured" : "Melona Work"].filter(Boolean),
  };
};

// ── Stats ──────────────────────────────────────────────────────
const STATS = [
  { icon: <FiPackage />, value: 500, suffix: "+", label: "Projects Completed" },
  { icon: <FiUsers />, value: 300, suffix: "+", label: "Happy Clients" },
  { icon: <FiAward />, value: 10, suffix: "+", label: "Years Experience" },
  { icon: <FiStar />, value: 98, suffix: "%", label: "Client Satisfaction" },
];

// ── Services ──────────────────────────────────────────────────
const SERVICES = [
  {
    category: "Digital Marketing",
    icon: <FiMonitor />,
    color: "#22A06B",
    items: ["Social Media Marketing", "Facebook & Instagram Ads", "Content Creation", "Brand Promotion", "SEO & Analytics"],
  },
  {
    category: "Printing Services",
    icon: <FiPrinter />,
    color: "#0F5D4F",
    items: ["T-Shirt Printing", "Business Cards", "Flyers & Brochures", "Stickers & Labels", "Banners & Posters"],
  },
  {
    category: "Advertising",
    icon: <FiTv />,
    color: "#D4AF37",
    items: ["Lightbox Advertising", "Outdoor Advertising", "Signboards", "Shop Branding", "Vehicle Branding"],
  },
  {
    category: "Event Management",
    icon: <FiCalendar />,
    color: "#22A06B",
    items: ["Corporate Events", "Product Launches", "Exhibition Setup", "Event Promotion", "Brand Activations"],
  },
];

// ── Why Choose Us ──────────────────────────────────────────────
const WHY_CHOOSE_US = [
  { icon: <FiAward />, title: "High Quality Work", description: "Premium materials and cutting-edge technology for exceptional results" },
  { icon: <FiZap />, title: "Fast Delivery", description: "Quick turnaround times without compromising on quality" },
  { icon: <FiDollarSign />, title: "Affordable Pricing", description: "Competitive rates that fit your budget and business needs" },
  { icon: <FiUsers />, title: "Professional Team", description: "Experienced experts dedicated to bringing your vision to life" },
  { icon: <FiTarget />, title: "Creative Solutions", description: "Innovative designs and strategies that make your brand stand out" },
  { icon: <FiHeart />, title: "Customer Satisfaction", description: "We don't stop until you're 100% happy with the results" },
];

// ── Testimonials ───────────────────────────────────────────────
const TESTIMONIALS = [
  { name: "Abebe Tadesse", role: "CEO, TechStart Ethiopia", image: "https://randomuser.me/api/portraits/men/32.jpg", rating: 5, text: "Melona transformed our brand identity with stunning designs and professional service. Their digital marketing expertise helped us reach thousands of new customers." },
  { name: "Hanna Bekele", role: "Owner, Café Addis", image: "https://randomuser.me/api/portraits/women/44.jpg", rating: 5, text: "The signboard and shop branding they created exceeded all expectations. Fast delivery, creative designs, and affordable pricing. Highly recommended!" },
  { name: "Michael Solomon", role: "Marketing Director", image: "https://randomuser.me/api/portraits/men/54.jpg", rating: 5, text: "Outstanding event management and promotional materials. They handled our product launch flawlessly from start to finish. True professionals!" },
];

const CONTACT_INFO = {
  phone: "0956565636",
  phoneAlt: "0956565646",
  email: "melona5656@gmail.com",
  address: "Debre Birhan, Atse Zeryakob Square, Kaleab Building, Ground Floor",
  whatsapp: "251956565636",
};

// ── Counter Animation Hook ────────────────────────────────────
function useCounter(target, isVisible) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target]);
  return count;
}

// ── Main Component ───────────────────────────────────────────
const NewHomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [countersVisible, setCountersVisible] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Melona Printing - Premium Printing & Advertising Services";
  }, []);
  const [formData, setFormData] = useState({ 
    name: "", 
    email: "", 
    phone: "",
    service: "digital_marketing",
    deadline: "",
    message: "" 
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitContactForm, { isLoading }] = useSubmitContactFormMutation();
  const { data: galleryData } = useGetGalleryItemsQuery();

  const statsRef = useRef(null);

  // Pre-calculate all counter values
  const counter0 = useCounter(STATS[0].value, countersVisible);
  const counter1 = useCounter(STATS[1].value, countersVisible);
  const counter2 = useCounter(STATS[2].value, countersVisible);
  const counter3 = useCounter(STATS[3].value, countersVisible);
  const counterValues = [counter0, counter1, counter2, counter3];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCountersVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => { setShowBackToTop(window.scrollY > 500); };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      await submitContactForm(formData).unwrap();
      setFormSubmitted(true);
      setTimeout(() => setFormSubmitted(false), 5000);
      setFormData({ 
        name: "", 
        email: "", 
        phone: "",
        service: "digital_marketing",
        deadline: "",
        message: "" 
      });
    } catch (error) {
      setFormError(error?.data?.message || "Failed to send message. Please try again.");
      console.error("Contact form error:", error);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const portfolioItems = useMemo(() => {
    const uploadedItems = galleryData?.data?.length ? galleryData.data : null;
    return (uploadedItems || PORTFOLIO_ITEMS).map(normalizePortfolioItem).filter((item) => item.image);
  }, [galleryData]);

  const portfolioCategories = useMemo(() => {
    const categories = portfolioItems.reduce((acc, item) => {
      if (!acc.some((category) => category.id === item.category)) {
        acc.push({
          id: item.category,
          label: item.categoryLabel,
          icon: CATEGORY_ICONS[item.category] || <FiImage />,
        });
      }
      return acc;
    }, []);

    return [{ id: "all", label: "All Projects", icon: <FiGrid /> }, ...categories];
  }, [portfolioItems]);

  const filteredPortfolio = activeFilter === "all"
    ? portfolioItems
    : portfolioItems.filter((item) => item.category === activeFilter);

  useEffect(() => {
    if (activeFilter !== "all" && !portfolioCategories.some((category) => category.id === activeFilter)) {
      setActiveFilter("all");
    }
  }, [activeFilter, portfolioCategories]);

  return (
    <div className="melona-premium">
      {/* ── Sticky Navigation ── */}
      <nav className={`melona-nav ${showBackToTop ? "scrolled" : ""}`}>
        <div className="melona-nav__inner">
          <Link to="/" className="melona-nav__logo">
            <img src={logo} alt="Melona" style={{ height: "40px" }} />
          </Link>
          <div className="melona-nav__links">
            <Link to="/">Home</Link>
            <Link to="/about">About</Link>
            <Link to="/services">Services</Link>
            <Link to="/#portfolio">Portfolio</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/login">Login</Link>
          </div>
          <Link to="/contact" className="melona-nav__cta">
            <FiPhone /> Get Quote
          </Link>
          <button className="melona-nav__toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="melona-mobile-menu">
            <nav>
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/services" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            </nav>
            <Link to="/contact" className="melona-btn melona-btn--primary melona-btn--full" style={{ marginTop: "var(--sp-4)" }} onClick={() => setMobileMenuOpen(false)}>
              <FiPhone /> Get Free Quote
            </Link>
          </div>
        )}
      </nav>

      {/* ── Hero Section ── */}
      <section className="melona-hero">
        <div className="melona-hero__bg">
          <img src={pexelsImages.hero} alt="Melona" className="melona-hero__bg-img" />
          <div className="melona-hero__overlay" />
          <div className="melona-hero__particles">
            {[...Array(6)].map((_, i) => <div key={i} className={`particle particle-${i + 1}`} />)}
          </div>
        </div>
        <div className="melona-hero__content">
          <div className="melona-hero__text">
            <div className="melona-hero__badge">
              <span className="pulse-dot" />
              Leading Printing & Advertising in Debre Birhan
            </div>
            <h1 className="melona-hero__title">
              Transform Your Brand with{" "}
              <span className="melona-hero__title-accent">Creative Excellence</span>
            </h1>
            <p className="melona-hero__subtitle">
              From stunning prints to powerful digital campaigns — premium printing, advertising,
              and marketing solutions that make your business shine.
            </p>
            <div className="melona-hero__actions">
              <Link to="/contact" className="melona-btn melona-btn--primary">
                Get Free Quote <FiArrowRight />
              </Link>
              <Link to="/#portfolio" className="melona-btn melona-btn--outline">
                View Portfolio
              </Link>
            </div>
          </div>
          <div className="melona-hero__card">
            <div className="melona-hero__card-header">
              <div className="melona-hero__card-icon"><FiCheckCircle /></div>
              <div>
                <h3>Quick Quote</h3>
                <p>Tell us what you need</p>
              </div>
            </div>
            <div className="melona-hero__card-items">
              {[
                { icon: <FiPrinter />, title: "Premium Printing", sub: "Business cards to large banners" },
                { icon: <FiMonitor />, title: "Digital Marketing", sub: "Social media & ad campaigns" },
                { icon: <FiTv />, title: "Advertising Solutions", sub: "Signboards & vehicle branding" },
                { icon: <FiCalendar />, title: "Event Management", sub: "Corporate events & launches" },
              ].map((item, i) => (
                <div key={i} className="melona-hero__card-item">
                  <div className="melona-hero__card-item-icon">{item.icon}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="melona-hero__stats" ref={statsRef}>
          {STATS.map((stat, i) => (
            <div key={i} className="melona-hero__stat">
              <span className="melona-hero__stat-value">
                {counterValues[i]}{stat.suffix}
              </span>
              <span className="melona-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── About Section ── */}
      <section className="melona-section" id="about">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiStar /> About Melona</span>
            <h2 className="melona-section-title">Debre Birhan's Premier Printing & Advertising Agency</h2>
            <p className="melona-section-subtitle">
              With over 10 years of experience, Melona has become the trusted partner for businesses seeking creative branding, professional printing, and impactful marketing solutions.
            </p>
          </div>
          
          {/* Modern Two-Column Layout */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "3rem",
            alignItems: "center",
            marginBottom: "3rem"
          }}>
            {/* Left: Image with Floating Badge */}
            <div style={{ position: "relative" }}>
              <div style={{
                position: "relative",
                borderRadius: "24px",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.12)"
              }}>
                <img 
                  src={banner} 
                  alt="Melona Team" 
                  style={{
                    width: "100%",
                    height: "500px",
                    objectFit: "cover",
                    display: "block"
                  }}
                />
                {/* Floating Award Badge */}
                <div style={{
                  position: "absolute",
                  bottom: "2rem",
                  left: "2rem",
                  right: "2rem",
                  background: "rgba(255,255,255,0.98)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.15)"
                }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #D4AF37 0%, #E5C158 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.75rem",
                    color: "#fff",
                    flexShrink: 0
                  }}>
                    <FiAward />
                  </div>
                  <div>
                    <strong style={{
                      display: "block",
                      fontSize: "1.125rem",
                      fontWeight: "700",
                      color: "#1A1A2E",
                      marginBottom: "0.25rem"
                    }}>
                      Award Winning
                    </strong>
                    <span style={{
                      fontSize: "0.9375rem",
                      color: "#6B7280"
                    }}>
                      Trusted by 300+ businesses
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Content Cards */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem"
            }}>
              {[
                { icon: <FiTarget />, title: "Our Mission", desc: "To empower businesses with exceptional creative solutions that drive growth and build lasting brand recognition.", color: "#0F5D4F" },
                { icon: <FiEye />, title: "Our Vision", desc: "To be Ethiopia's leading creative agency, known for innovation, quality, and outstanding customer service.", color: "#22A06B" },
                { icon: <FiThumbsUp />, title: "Our Values", desc: "Quality, integrity, creativity, and customer satisfaction guide everything we do.", color: "#D4AF37" },
              ].map((item, i) => (
                <div 
                  key={i} 
                  style={{
                    background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
                    borderRadius: "20px",
                    padding: "2rem",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.5rem",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateX(8px)";
                    e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
                  }}
                >
                  <div style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "16px",
                    background: item.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.75rem",
                    color: "#fff",
                    flexShrink: 0,
                    boxShadow: `0 8px 20px ${item.color}40`
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: "1.375rem",
                      fontWeight: "700",
                      color: "#1A1A2E",
                      marginBottom: "0.625rem",
                      lineHeight: "1.3"
                    }}>
                      {item.title}
                    </h3>
                    <p style={{
                      fontSize: "1rem",
                      color: "#6B7280",
                      lineHeight: "1.7",
                      margin: 0
                    }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Experience Stats Row */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1.5rem",
            marginTop: "3rem"
          }}>
            {[
              { value: "10+", label: "Years Experience", icon: <FiCalendar />, color: "#0F5D4F" },
              { value: "300+", label: "Happy Clients", icon: <FiUsers />, color: "#22A06B" },
              { value: "500+", label: "Projects Completed", icon: <FiPackage />, color: "#D4AF37" },
              { value: "98%", label: "Satisfaction Rate", icon: <FiThumbsUp />, color: "#0F5D4F" },
            ].map((stat, i) => (
              <div 
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: "20px",
                  padding: "2rem",
                  textAlign: "center",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = "0 12px 35px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
                }}
              >
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  background: stat.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  color: "#fff",
                  margin: "0 auto 1rem",
                  boxShadow: `0 8px 20px ${stat.color}40`
                }}>
                  {stat.icon}
                </div>
                <div style={{
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  background: `linear-gradient(135deg, ${stat.color}, ${stat.color}CC)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  marginBottom: "0.5rem"
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: "0.9375rem",
                  color: "#6B7280",
                  fontWeight: "600"
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Section ── */}
      <section className="melona-section melona-section--gray" id="services">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiShoppingBag /> Our Services</span>
            <h2 className="melona-section-title">Comprehensive Solutions for Your Business</h2>
            <p className="melona-section-subtitle">From concept to execution, we provide end-to-end services that help your brand make a lasting impression.</p>
          </div>
          
          {/* 4 Column Grid */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", 
            gap: "1.5rem"
          }}>
            {SERVICES.map((service, i) => (
              <div 
                key={i} 
                style={{
                  background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
                  borderRadius: "20px",
                  padding: "2rem",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = "0 15px 45px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.08)";
                }}
              >
                {/* Accent Border Top */}
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: `linear-gradient(90deg, ${service.color}, ${service.color}CC)`
                }} />
                
                {/* Icon */}
                <div style={{
                  width: "70px",
                  height: "70px",
                  borderRadius: "16px",
                  background: service.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "1.75rem",
                  marginBottom: "1.25rem",
                  boxShadow: `0 10px 25px ${service.color}40`
                }}>
                  {service.icon}
                </div>
                
                {/* Title */}
                <h3 style={{
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: "#1A1A2E",
                  marginBottom: "1rem",
                  lineHeight: "1.3"
                }}>
                  {service.category}
                </h3>
                
                {/* Features List */}
                <ul style={{
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.625rem",
                  marginBottom: "1.25rem",
                  flex: 1
                }}>
                  {service.items.map((item, j) => (
                    <li key={j} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.875rem",
                      color: "#6B7280"
                    }}>
                      <FiCheck style={{ color: "#22A06B", fontSize: "1rem", flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Learn More Link */}
                <Link 
                  to="/services" 
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: service.color,
                    transition: "gap 0.25s ease",
                    marginTop: "auto"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.gap = "0.75rem"}
                  onMouseLeave={(e) => e.currentTarget.style.gap = "0.5rem"}
                >
                  Learn More <FiChevronRight />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Portfolio Section ── */}
      <section className="melona-section" id="portfolio">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiImage /> Our Portfolio</span>
            <h2 className="melona-section-title">Projects That Speak for Themselves</h2>
            <p className="melona-section-subtitle">Explore uploaded work from the Melona admin gallery across printing, branding, advertising, and campaigns.</p>
          </div>
          <div className="melona-portfolio-filters">
            {portfolioCategories.map((cat) => (
              <button
                key={cat.id}
                className={`melona-filter-btn ${activeFilter === cat.id ? "active" : ""}`}
                onClick={() => setActiveFilter(cat.id)}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          <div className="melona-portfolio-grid">
            {filteredPortfolio.map((item) => (
              <div key={item.id} className="melona-portfolio-item">
                <img src={item.image} alt={item.title} />
                <div className="melona-portfolio-item__overlay">
                  <h3>{item.title}</h3>
                  {item.description && <p>{item.description}</p>}
                  <div className="melona-portfolio-item__tags">
                    {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "2.5rem" }}>
            <Link to="/gallery" className="melona-btn melona-btn--primary">
              View Full Gallery <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="melona-section melona-section--gray" id="why">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiStar /> Why Choose Melona</span>
            <h2 className="melona-section-title">Excellence in Every Project</h2>
            <p className="melona-section-subtitle">We combine creativity, technology, and dedication to deliver results that exceed expectations.</p>
          </div>
          <div className="melona-why-grid">
            {WHY_CHOOSE_US.map((item, i) => (
              <div key={i} className="melona-why-card">
                <div className="melona-why-card__icon">{item.icon}</div>
                <h3 className="melona-why-card__title">{item.title}</h3>
                <p className="melona-why-card__desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="melona-section" id="testimonials">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiMessageCircle /> Testimonials</span>
            <h2 className="melona-section-title">What Our Clients Say</h2>
            <p className="melona-section-subtitle">Don't just take our word for it — hear from businesses we've helped grow.</p>
          </div>
          <div className="melona-testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="melona-testimonial-card">
                <div className="melona-testimonial-card__stars">
                  {[...Array(t.rating)].map((_, j) => <FiStar key={j} fill="#D4AF37" stroke="#D4AF37" />)}
                </div>
                <p className="melona-testimonial-card__text">"{t.text}"</p>
                <div className="melona-testimonial-card__author">
                  <img src={t.image} alt={t.name} />
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact Section ── */}
      <section className="melona-section melona-section--dark" id="contact">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow melona-eyebrow--light"><FiPhone /> Get in Touch</span>
            <h2 className="melona-section-title melona-section-title--light">Ready to Elevate Your Brand?</h2>
            <p className="melona-section-subtitle melona-section-subtitle--light">Let's discuss your project and create something amazing together.</p>
          </div>
          <div className="melona-contact-grid">
            <div className="melona-contact-info">
              <div className="melona-contact-card">
                <div className="melona-contact-card__icon"><FiPhone /></div>
                <div>
                  <h4>Call Us</h4>
                  <p>{CONTACT_INFO.phone} / {CONTACT_INFO.phoneAlt}</p>
                </div>
              </div>
              <div className="melona-contact-card">
                <div className="melona-contact-card__icon"><FiMail /></div>
                <div>
                  <h4>Email</h4>
                  <p>{CONTACT_INFO.email}</p>
                </div>
              </div>
              <div className="melona-contact-card">
                <div className="melona-contact-card__icon"><FiMapPin /></div>
                <div>
                  <h4>Visit Us</h4>
                  <p>{CONTACT_INFO.address}</p>
                </div>
              </div>
              <a href={`https://wa.me/${CONTACT_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer" className="melona-whatsapp-btn">
                <FiMessageCircle /> Chat on WhatsApp
              </a>
            </div>
            <div className="melona-contact-form-wrap">
              <form className="melona-contact-form" onSubmit={handleFormSubmit}>
                {formError && (
                  <div style={{
                    padding: "0.875rem 1rem",
                    background: "#fee2e2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    color: "#dc2626",
                    fontSize: "0.875rem",
                    marginBottom: "1rem"
                  }}>
                    {formError}
                  </div>
                )}
                
                <div className="melona-form-group">
                  <label>Your Name</label>
                  <input 
                    type="text" 
                    placeholder="Abebe Kebede" 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    required 
                  />
                </div>
                
                <div className="melona-form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    placeholder="you@example.com" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    required 
                  />
                </div>
                
                <div className="melona-form-group">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    placeholder="+251 9XX XXX XXX" 
                    value={formData.phone} 
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                    required 
                  />
                </div>
                
                <div className="melona-form-group">
                  <label>Service Interested In</label>
                  <select 
                    value={formData.service} 
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      fontSize: "1rem",
                      color: "#1f2937",
                      background: "#fff",
                      cursor: "pointer"
                    }}
                  >
                    <option value="digital_marketing">Digital Marketing</option>
                    <option value="printing">Printing Services</option>
                    <option value="advertising">Advertising</option>
                    <option value="event_management">Event Management</option>
                    <option value="branding">Branding & Design</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="melona-form-group">
                  <label>Project Deadline (Optional)</label>
                  <input 
                    type="date" 
                    value={formData.deadline} 
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      fontSize: "1rem",
                      color: "#1f2937"
                    }}
                  />
                </div>
                
                <div className="melona-form-group">
                  <label>Your Message</label>
                  <textarea 
                    rows="5" 
                    placeholder="Tell us about your project..." 
                    value={formData.message} 
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })} 
                    required 
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="melona-btn melona-btn--gold melona-btn--full"
                  disabled={isLoading}
                  style={{ opacity: isLoading ? 0.7 : 1 }}
                >
                  {isLoading ? "Sending..." : formSubmitted ? "✓ Message Sent!" : "Send Message"} <FiArrowRight />
                </button>
              </form>
            </div>
          </div>
          <div className="melona-map-wrap">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.5!2d39.5!3d9.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOcKwNDInMDQuMCJOIDM5wrAzMCcwLjAiZg!5e0!3m2!1sen!2set!4v1"
              width="100%"
              height="350"
              style={{ border: 0, borderRadius: "16px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Melona Location"
            />
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="melona-footer">
        <div className="melona-container">
          <div className="melona-footer__grid">
            <div className="melona-footer__brand">
              <div className="melona-footer__logo">
                <img src={logo} alt="Melona" style={{ height: "50px" }} />
              </div>
              <p>Your trusted partner for premium printing, advertising, and digital marketing solutions in Debre Birhan and beyond.</p>
              <div className="melona-footer__social">
                <a href="#" aria-label="Instagram"><FiInstagram /></a>
                <a href="#" aria-label="LinkedIn"><FiLinkedin /></a>
                <a href="#" aria-label="Website"><FiGlobe /></a>
              </div>
            </div>
            <div className="melona-footer__col">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/services">Services</Link></li>
                <li><Link to="/#portfolio">Portfolio</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div className="melona-footer__col">
              <h4>Services</h4>
              <ul>
                <li><Link to="/services">Digital Marketing</Link></li>
                <li><Link to="/services">Printing Services</Link></li>
                <li><Link to="/services">Advertising</Link></li>
                <li><Link to="/services">Event Management</Link></li>
                <li><Link to="/services">Brand Design</Link></li>
              </ul>
            </div>
            <div className="melona-footer__col">
              <h4>Contact</h4>
              <ul>
                <li><FiPhone /> {CONTACT_INFO.phone}</li>
                <li><FiMail /> {CONTACT_INFO.email}</li>
                <li><FiMapPin /> Debre Birhan, Ethiopia</li>
              </ul>
            </div>
          </div>
          <div className="melona-footer__bottom">
            <p>© {new Date().getFullYear()} Melona Printing. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── Back to Top ── */}
      <button className={`melona-back-to-top ${showBackToTop ? "visible" : ""}`} onClick={scrollToTop} aria-label="Back to top">
        <FiChevronUp />
      </button>
    </div>
  );
};

export default NewHomePage;
